/**
 * Storico WhatsApp del cliente per la cronologia (vista 7, design congelato 12 ago).
 *
 * Matching a TRE livelli, zero campi nuovi (in ordine di precedenza):
 *  1. link manuale dell'assignment (`whatsapp_chat_assignments.client_id/deal_id`,
 *     stesso canale del tool MCP `crm_whatsapp_chat_link`) — vince sempre;
 *  2. `whatsapp_messages.client_id` già popolato dall'inbound;
 *  3. match per NUMERO normalizzato su tutti i campi telefono raggiungibili dalla
 *     scheda: `clients.phone` + `clients.whatsapp_number`, `client_contacts.phone`
 *     dei referenti attivi, e phone/whatsapp_number delle PERSONE collegate
 *     (relazioni first-class: `client_relationships` type='contatto').
 *
 * Visibilità: si interrogano SOLO le config accessibili all'utente corrente
 * (stesso filtro per-utente `whatsapp_user_access` della pagina WhatsApp: le
 * config arrivano già filtrate da `useWhatsAppConfigs`). Decrypt CLIENT-SIDE
 * come nella pagina WhatsApp (`decryptWhatsAppMessage`), nessun endpoint nuovo.
 *
 * Le sessioni sono tagliate dalla regola direzionale di `whatsappSessions.ts`.
 */

import { supabase } from '../supabase';
import { decryptWhatsAppMessage, isEncrypted } from '../utils/crypto';
import { sessionizeMessages } from '../utils/whatsappSessions';

/** Stessi tipi di sistema filtrati dalla pagina WhatsApp (whatsappDbService). */
const SYSTEM_MESSAGE_TYPES = [
  'e2e_notification', 'notification_template', 'protocol',
  'unknown', 'gp2', 'call_log', 'notification',
];

/** Etichette per i messaggi media (il content non è testo decifrabile). */
const MEDIA_LABELS: Record<string, string> = {
  image: '[Immagine]',
  video: '[Video]',
  audio: '[Messaggio vocale]',
  ptt: '[Messaggio vocale]',
  voice: '[Messaggio vocale]',
  sticker: '[Sticker]',
  document: '[Documento]',
  location: '[Posizione]',
  contact: '[Contatto]',
};

export interface ClientWhatsAppMessage {
  id: string;
  direction: 'inbound' | 'outbound';
  timestamp: Date;
  content: string;
  /** Nome dell'utente CRM che ha inviato (solo outbound, best-effort). */
  senderName?: string;
}

export interface ClientWhatsAppSession {
  /** Chiave stabile per React: config|chat|start. */
  key: string;
  configId: string;
  /** Nome del numero aziendale (display name o numero della config). */
  configLabel: string;
  /** JID della chat per "Apri in WhatsApp" (es. 39333...@c.us). */
  chatJid: string;
  /** Nome mostrato (cliente/referente/persona collegata o numero). */
  chatName: string;
  /** Numero formattato da mostrare accanto al nome ('' per i gruppi). */
  chatNumber: string;
  isGroup: boolean;
  startAt: string;
  endAt: string;
  messageCount: number;
  inboundCount: number;
  outboundCount: number;
  lastPreview: string;
  /** Messaggi in ordine cronologico ascendente (già decifrati). */
  messages: ClientWhatsAppMessage[];
}

export interface AccessibleWhatsAppConfig {
  id: string;
  label: string;
}

/** Solo cifre (stessa semantica di normalizePhone in whatsappDbService). */
function digits(value: string | null | undefined): string {
  if (!value) return '';
  return value.replace(/\D/g, '');
}

/**
 * Varianti di un numero per il confronto con i JID (che sono E.164 senza +):
 * cifre pure, senza il prefisso 00 e con il default italiano 39 quando manca.
 */
function phoneVariants(value: string | null | undefined): string[] {
  let d = digits(value);
  if (!d) return [];
  if (d.startsWith('00')) d = d.slice(2);
  const variants = new Set<string>([d]);
  // Numeri nazionali senza prefisso: i JID WhatsApp lo hanno sempre.
  if (d.length <= 10 && !d.startsWith('39')) variants.add(`39${d}`);
  return Array.from(variants).filter((v) => v.length >= 8);
}

function formatDisplayNumber(d: string): string {
  if (!d) return '';
  if (d.startsWith('39') && d.length > 10) {
    const rest = d.slice(2);
    return `+39 ${rest.slice(0, 3)} ${rest.slice(3, 6)} ${rest.slice(6)}`.trim();
  }
  return `+${d}`;
}

interface RawMessageRecord {
  id: string;
  config_id: string;
  from_jid: string | null;
  to_jid: string | null;
  chat_identifier_e164: string | null;
  direction: 'inbound' | 'outbound';
  content: string | null;
  type: string | null;
  media_url: string | null;
  media_mimetype: string | null;
  created_at: string;
  sender_user_id: string | null;
  is_deleted: boolean | null;
  metadata?: { kind?: string } | null;
}

/** Identità della chat a cui appartiene il record (jid + cifre per il matching). */
function chatIdentity(record: RawMessageRecord): { jid: string; digits: string; isGroup: boolean } | null {
  const rawJid = record.direction === 'inbound' ? record.from_jid : (record.to_jid || record.from_jid);
  if (record.chat_identifier_e164) {
    return { jid: `${record.chat_identifier_e164}@c.us`, digits: record.chat_identifier_e164, isGroup: false };
  }
  if (!rawJid) return null;
  const isGroup = rawJid.includes('@g.us');
  const bare = rawJid.split('@')[0];
  if (isGroup) return { jid: rawJid.includes('@') ? rawJid : `${bare}@g.us`, digits: bare, isGroup: true };
  // status/newsletter non sono chat cliente
  if (rawJid.includes('@newsletter') || rawJid.startsWith('status@')) return null;
  return { jid: rawJid.includes('@') ? rawJid : `${bare}@c.us`, digits: bare, isGroup: false };
}

async function decryptContent(record: RawMessageRecord, organizationId: string): Promise<string> {
  const mediaLabel = record.type ? MEDIA_LABELS[record.type] : undefined;
  const hasMedia = !!record.media_url || !!mediaLabel || !!(record.media_mimetype && !record.media_mimetype.startsWith('text/'));
  if (record.is_deleted) return '[Messaggio eliminato]';
  if (hasMedia) return mediaLabel || '[Allegato]';
  let content = record.content || '';
  try {
    if (content && isEncrypted(content)) {
      content = await decryptWhatsAppMessage(content, organizationId);
    }
  } catch {
    content = '[Messaggio non leggibile]';
  }
  return content;
}

/** Massimo di messaggi letti dal DB (le sessioni si costruiscono sugli ultimi). */
const MESSAGES_FETCH_LIMIT = 400;
/** Massimo di sessioni restituite alla timeline. */
const SESSIONS_LIMIT = 30;

export async function fetchClientWhatsAppSessions(params: {
  clientId: string;
  organizationId: string;
  configs: AccessibleWhatsAppConfig[];
  dealId?: string | null;
}): Promise<ClientWhatsAppSession[]> {
  const { clientId, organizationId, configs, dealId } = params;
  if (!clientId || !organizationId || configs.length === 0) return [];

  const configIds = configs.map((c) => c.id);
  const configLabelById = new Map(configs.map((c) => [c.id, c.label]));

  // ── Livello 1: link manuali (assignment) ─────────────────────────────────
  const assignmentOr = dealId
    ? `client_id.eq.${clientId},deal_id.eq.${dealId}`
    : `client_id.eq.${clientId}`;
  const { data: assignments } = await supabase
    .from('whatsapp_chat_assignments')
    .select('config_id, chat_jid, chat_name, chat_type')
    .eq('organization_id', organizationId)
    .in('config_id', configIds)
    .or(assignmentOr);

  const linkedChats = (assignments || []).filter((a) => a.chat_jid);
  const linkedNameByJid = new Map<string, string>();
  for (const a of linkedChats) {
    if (a.chat_name) linkedNameByJid.set(a.chat_jid, a.chat_name);
  }

  // ── Livello 3: numeri raggiungibili dalla scheda ─────────────────────────
  const nameByDigits = new Map<string, string>();
  const allVariants = new Set<string>();
  const addPhones = (label: string | null | undefined, ...values: (string | null | undefined)[]) => {
    for (const value of values) {
      for (const v of phoneVariants(value)) {
        allVariants.add(v);
        if (label && !nameByDigits.has(v)) nameByDigits.set(v, label);
      }
    }
  };

  const [{ data: clientRow }, { data: contacts }, { data: relations }] = await Promise.all([
    supabase
      .from('clients')
      .select('business_name, phone, whatsapp_number')
      .eq('id', clientId)
      .eq('organization_id', organizationId)
      .maybeSingle(),
    supabase
      .from('client_contacts')
      .select('name, phone')
      .eq('client_id', clientId)
      .eq('organization_id', organizationId)
      .eq('is_active', true)
      .not('phone', 'is', null),
    supabase
      .from('client_relationships')
      .select('source_client_id, target_client_id')
      .eq('organization_id', organizationId)
      .eq('relationship_type', 'contatto')
      .eq('is_active', true)
      .or(`source_client_id.eq.${clientId},target_client_id.eq.${clientId}`),
  ]);

  addPhones(clientRow?.business_name, clientRow?.phone, clientRow?.whatsapp_number);
  for (const contact of contacts || []) addPhones(contact.name, contact.phone);

  const relatedIds = Array.from(
    new Set(
      (relations || [])
        .flatMap((r) => [r.source_client_id, r.target_client_id])
        .filter((id): id is string => !!id && id !== clientId),
    ),
  );
  if (relatedIds.length > 0) {
    const { data: persons } = await supabase
      .from('clients')
      .select('business_name, phone, whatsapp_number')
      .in('id', relatedIds)
      .eq('organization_id', organizationId);
    for (const person of persons || []) {
      addPhones(person.business_name, person.phone, person.whatsapp_number);
    }
  }

  // ── Query messaggi: livelli 1+2+3 in un solo OR ──────────────────────────
  const orParts: string[] = [`client_id.eq.${clientId}`];
  const variantList = Array.from(allVariants);
  if (variantList.length > 0) {
    const inList = variantList.join(',');
    const jidList = variantList.map((v) => `${v}@c.us`).join(',');
    orParts.push(`from_jid.in.(${jidList})`);
    orParts.push(`to_jid.in.(${inList})`);
    orParts.push(`chat_identifier_e164.in.(${inList})`);
  }
  for (const linked of linkedChats) {
    const bare = linked.chat_jid.split('@')[0];
    orParts.push(`from_jid.eq.${linked.chat_jid}`);
    orParts.push(`to_jid.eq.${bare}`);
  }

  const { data: rawMessages, error } = await supabase
    .from('whatsapp_messages')
    .select('id, config_id, from_jid, to_jid, chat_identifier_e164, direction, content, type, media_url, media_mimetype, created_at, sender_user_id, is_deleted, metadata')
    .eq('organization_id', organizationId)
    .in('config_id', configIds)
    // IS NOT TRUE: include anche i record storici con is_newsletter null
    .not('is_newsletter', 'is', true)
    .or(orParts.join(','))
    .order('created_at', { ascending: false })
    .limit(MESSAGES_FETCH_LIMIT);

  if (error) throw error;
  if (!rawMessages || rawMessages.length === 0) return [];

  const usable = (rawMessages as RawMessageRecord[]).filter(
    (r) => r.metadata?.kind === 'call' || !SYSTEM_MESSAGE_TYPES.includes(r.type || ''),
  );
  if (usable.length === 0) return [];

  // Nomi degli autori outbound (utenti CRM), in un solo batch.
  const senderIds = Array.from(
    new Set(usable.map((r) => r.sender_user_id).filter((id): id is string => !!id)),
  );
  const senderNameById = new Map<string, string>();
  if (senderIds.length > 0) {
    const { data: profiles } = await supabase
      .from('user_profiles')
      .select('id, full_name')
      .in('id', senderIds);
    for (const p of profiles || []) {
      if (p.full_name) senderNameById.set(p.id, p.full_name);
    }
  }

  // ── Raggruppa per chat, decifra, sessionizza ─────────────────────────────
  interface ChatBucket {
    jid: string;
    digits: string;
    isGroup: boolean;
    configId: string;
    messages: (ClientWhatsAppMessage & { direction: 'inbound' | 'outbound' })[];
  }
  const buckets = new Map<string, ChatBucket>();

  for (const record of usable) {
    const identity = chatIdentity(record);
    if (!identity) continue;
    const key = `${record.config_id}|${identity.isGroup ? identity.jid : identity.digits}`;
    let bucket = buckets.get(key);
    if (!bucket) {
      bucket = { ...identity, configId: record.config_id, messages: [] };
      buckets.set(key, bucket);
    }
    bucket.messages.push({
      id: record.id,
      direction: record.direction,
      timestamp: new Date(record.created_at),
      content: await decryptContent(record, organizationId),
      senderName:
        record.direction === 'outbound' && record.sender_user_id
          ? senderNameById.get(record.sender_user_id)
          : undefined,
    });
  }

  const sessions: ClientWhatsAppSession[] = [];
  for (const bucket of buckets.values()) {
    const chatName =
      linkedNameByJid.get(bucket.jid) ||
      nameByDigits.get(bucket.digits) ||
      clientRow?.business_name ||
      (bucket.isGroup ? 'Gruppo WhatsApp' : formatDisplayNumber(bucket.digits));
    const chatNumber = bucket.isGroup ? '' : formatDisplayNumber(bucket.digits);
    const configLabel = configLabelById.get(bucket.configId) || '';

    for (const group of sessionizeMessages(bucket.messages)) {
      const last = group.messages[group.messages.length - 1];
      sessions.push({
        key: `${bucket.configId}|${bucket.jid}|${group.startAt.getTime()}`,
        configId: bucket.configId,
        configLabel,
        chatJid: bucket.jid,
        chatName,
        chatNumber,
        isGroup: bucket.isGroup,
        startAt: group.startAt.toISOString(),
        endAt: group.endAt.toISOString(),
        messageCount: group.messages.length,
        inboundCount: group.messages.filter((m) => m.direction === 'inbound').length,
        outboundCount: group.messages.filter((m) => m.direction === 'outbound').length,
        lastPreview: last.content.replace(/\s+/g, ' ').trim().slice(0, 160),
        messages: group.messages,
      });
    }
  }

  return sessions
    .sort((a, b) => new Date(b.endAt).getTime() - new Date(a.endAt).getTime())
    .slice(0, SESSIONS_LIMIT);
}
